// Iteration 088 -- Public policy page (privacy / terms / families)
import { useEffect, useState } from "react";
import type { PolicyType, PolicyVersionPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

interface PolicyPageProps {
  type: PolicyType;
  title: string;
}

const headings: Record<PolicyType, string> = {
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  families: "Families Policy",
};

export default function PolicyPage({ type, title }: PolicyPageProps) {
  const [policy, setPolicy] = useState<PolicyVersionPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .getCurrentPolicy(type)
      .then((r) => setPolicy(r.policy))
      .catch((e) => {
        if (e instanceof api.ApiError && e.status === 404) {
          setError("This policy has not been published yet.");
        } else {
          setError(e instanceof Error ? e.message : "Failed to load policy");
        }
      })
      .finally(() => setLoading(false));
  }, [type]);

  if (loading) return <p data-page="policy" data-state="loading">Loading…</p>;

  return (
    <div className="mx-auto max-w-3xl py-8" data-page="policy" data-policy-type={type}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{title ?? headings[type]}</CardTitle>
          {policy && (
            <p className="text-xs text-muted-foreground">
              Version {policy.version} · Effective {new Date(policy.effective_at).toLocaleDateString()}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {error ? (
            <p className="text-sm text-muted-foreground" data-state="empty">{error}</p>
          ) : policy ? (
            <article className="prose prose-sm max-w-none whitespace-pre-wrap">
              {policy.body_markdown}
            </article>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
